import * as React from 'react';
import { useSelector } from 'react-redux';

import { getDateRangeValue, getRangeValue } from '../../../../packages/JsonQuery';
import { useApplicationContext } from '../../containers/ApplicationContext';
import { IDailyRentOfferForMobileSite } from '../../repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectResultsJsonQuery } from '../../selectors/results';
import { ITrackOfferActionsProps } from '../../types/tracking';
import { prepareOfferUrl } from '../../utils/prepareOfferUrl';
import { useTrackingProps } from '../useTrackingProps';

interface IUseOfferPreviewDataParams {
  offer: IDailyRentOfferForMobileSite;
  reason: ITrackOfferActionsProps['reason'];
}

interface IOfferPreviewData {
  offerLink: string;
  hasDates: boolean;
  showFavoriteButton: boolean;
  trackingProps: ITrackOfferActionsProps;
}

export function useOfferPreviewData({ offer, reason }: IUseOfferPreviewDataParams): IOfferPreviewData {
  const { config } = useApplicationContext();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const isPartnerFavoritesEnabled = !!config.get<boolean>('dailyrent.partners_favorites.Enabled');

  const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);
  const checkInDate = jsonQueryDates?.gte;
  const checkOutDate = jsonQueryDates?.lt;
  const minBeds = getRangeValue('beds_count')(jsonQuery)?.gte;
  const trackingProps = useTrackingProps({ offer, offerPosition: 1, offersLength: 1, reason });
  const showFavoriteButton = (!offer.isCianPartner || isPartnerFavoritesEnabled) && !offer.isHotel;
  const offerLink = prepareOfferUrl({
    offer,
    checkInDate,
    checkOutDate,
    minBeds,
  });

  return React.useMemo(
    () => ({
      offerLink,
      hasDates: Boolean(jsonQueryDates),
      showFavoriteButton,
      trackingProps,
    }),
    [offerLink, jsonQueryDates, showFavoriteButton, trackingProps],
  );
}
